import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { RangerAvatar } from '../components/RangerAvatar';
import {
    Leaf,
    Plus,
    Users,
    Target,
    Star,
    SignOut,
    User,
    Check,
    CaretRight,
    UserPlus,
    EnvelopeSimple,
    GraduationCap,
    Printer,
    Gear
} from '@phosphor-icons/react';

const AVATARS = [
    { id: 'koala', emoji: '🐨', name: 'Koala' },
    { id: 'kangaroo', emoji: '🦘', name: 'Kangaroo' },
    { id: 'wombat', emoji: '🐻', name: 'Wombat' },
    { id: 'platypus', emoji: '🦆', name: 'Platypus' },
    { id: 'emu', emoji: '🐦', name: 'Emu' }
];

export default function ParentDashboard() {
    const { user, logout, fetchChildren, children, createChild, switchToChild, api } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [showAddChild, setShowAddChild] = useState(false);
    const [newChildName, setNewChildName] = useState('');
    const [newChildAge, setNewChildAge] = useState('');
    const [newChildAvatar, setNewChildAvatar] = useState('koala');
    const [creatingChild, setCreatingChild] = useState(false);
    const [impact, setImpact] = useState(null);

    // Add parent state (now invite-based)
    const [showAddParent, setShowAddParent] = useState(false);
    const [linkedParents, setLinkedParents] = useState([]);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteName, setInviteName] = useState('');
    const [inviting, setInviting] = useState(false);

    // Email verification state
    const [showVerification, setShowVerification] = useState(false);
    const [verifyCode, setVerifyCode] = useState('');
    const [verifying, setVerifying] = useState(false);
    const [resending, setResending] = useState(false);

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadData = async () => {
        try {
            await fetchChildren();
            const [impactRes, linkedRes] = await Promise.all([
                api().get('/impact'),
                api().get('/auth/linked-parents')
            ]);
            setImpact(impactRes.data);
            setLinkedParents(linkedRes.data);
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/');
        toast.success('Logged out successfully');
    };

    const handleCreateChild = async (e) => {
        e.preventDefault();
        if (!newChildName || !newChildAge) {
            toast.error('Please fill in all fields');
            return;
        }
        
        const age = parseInt(newChildAge);
        if (age < 4 || age > 18) {
            toast.error('Please enter a valid age');
            return;
        }

        setCreatingChild(true);
        try {
            await createChild(newChildName, age, newChildAvatar);
            toast.success(`${newChildName}'s profile created!`);
            setShowAddChild(false);
            setNewChildName('');
            setNewChildAge('');
            setNewChildAvatar('koala');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create child profile');
        } finally {
            setCreatingChild(false);
        }
    };

    const handleSwitchToChild = async (childId) => {
        try {
            await switchToChild(childId);
            toast.success('Switched to child account');
            navigate('/dashboard');
        } catch (error) {
            toast.error('Failed to switch account');
        }
    };

    const handleInviteParent = async (e) => {
        e.preventDefault();
        if (!inviteEmail) { toast.error('Please enter an email address'); return; }
        setInviting(true);
        try {
            await api().post('/parent/invite', { email: inviteEmail, name: inviteName });
            toast.success(`Invitation sent to ${inviteEmail}`);
            setShowAddParent(false);
            setInviteEmail('');
            setInviteName('');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to send invitation');
        } finally {
            setInviting(false);
        }
    };

    const handleVerifyEmail = async (e) => {
        e.preventDefault();
        if (!verifyCode || verifyCode.length !== 6) { toast.error('Please enter the 6-digit code'); return; }
        setVerifying(true);
        try {
            await api().post('/auth/verify-email', { code: verifyCode });
            toast.success('Email verified!');
            setShowVerification(false);
            // Refresh user data
            window.location.reload();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Invalid code');
        } finally {
            setVerifying(false);
        }
    };

    const handleResendVerification = async () => {
        setResending(true);
        try {
            await api().post('/auth/resend-verification');
            toast.success('New verification code sent!');
        } catch (error) {
            toast.error('Failed to resend code');
        } finally {
            setResending(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[hsl(53,92%,96%)] flex items-center justify-center">
                <div className="brutalist-card p-8 text-center">
                    <div className="w-16 h-16 border-4 border-[#22c55e] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="font-fredoka text-lg">Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#fefce8] bg-[radial-gradient(#cbd5e1_1.5px,transparent_1.5px)] [background-size:24px_24px]">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b-4 border-[#0f172a]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-20">
                        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
                            <div className="w-10 h-10 rounded-full bg-[#22c55e] border-4 border-[#0f172a] flex items-center justify-center">
                                <Leaf weight="fill" className="text-white" size={20} />
                            </div>
                            <span className="font-fredoka text-xl font-bold text-[#0f172a] hidden sm:block">
                                Parent Portal
                            </span>
                        </Link>
                        
                        <div className="flex items-center gap-3">
                            <Link
                                to="/educator"
                                className="bg-[#fde047] text-[#0f172a] border-4 border-[#0f172a] shadow-[4px_4px_0px_#0f172a] rounded-xl font-bold px-4 py-2 text-sm flex items-center gap-1.5 hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[6px_6px_0px_#0f172a] active:translate-y-0 active:translate-x-0 active:shadow-[2px_2px_0px_#0f172a] transition-all"
                                data-testid="educator-portal-link"
                            >
                                <GraduationCap weight="bold" size={18} />
                                <span className="hidden sm:inline">Educator Portal</span>
                            </Link>
                            <span className="text-[#334155] font-bold hidden sm:block">
                                {user?.name}
                            </span>
                            <Link
                                to="/account"
                                className="relative p-2 rounded-full hover:bg-gray-100"
                                data-testid="account-settings-link"
                                title="Account Settings"
                            >
                                <Gear weight="bold" size={22} className="text-[#0f172a]" />
                                {user?.email_verified === false && (
                                    <span className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-[#ef4444] border-2 border-white" data-testid="account-unverified-dot" />
                                )}
                            </Link>
                            <motion.button
                                onClick={handleLogout}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="p-2 rounded-full hover:bg-gray-100"
                                data-testid="logout-btn"
                            >
                                <SignOut weight="bold" size={24} className="text-[#0f172a]" />
                            </motion.button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <main className="pt-28 px-6 md:px-12 pb-12 max-w-7xl mx-auto">
                {/* Email Verification Banner */}
                {user?.email_verified === false && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-8 bg-[#fef3c7] border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-2xl p-5"
                        data-testid="verify-email-banner"
                    >
                        {!showVerification ? (
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                                <div>
                                    <p className="font-fredoka font-bold text-[#0f172a]">Verify your email</p>
                                    <p className="text-sm text-[#475569] font-semibold">Check your inbox for a 6-digit verification code.</p>
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => setShowVerification(true)}
                                        className="bg-[#f59e0b] text-white border-4 border-[#0f172a] shadow-[4px_4px_0px_#0f172a] rounded-xl font-bold px-4 py-2 text-sm hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[6px_6px_0px_#0f172a] transition-all" data-testid="enter-code-btn">
                                        Enter Code
                                    </button>
                                    <button onClick={handleResendVerification} disabled={resending}
                                        className="bg-white text-[#0f172a] border-4 border-[#0f172a] shadow-[4px_4px_0px_#0f172a] rounded-xl font-bold px-4 py-2 text-sm disabled:opacity-50 hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[6px_6px_0px_#0f172a] transition-all" data-testid="resend-code-btn">
                                        {resending ? 'Sending...' : 'Resend'}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <form onSubmit={handleVerifyEmail} className="space-y-3">
                                <p className="font-fredoka font-bold text-[#0f172a]">Enter verification code</p>
                                <div className="flex gap-2">
                                    <input
                                        type="text" value={verifyCode}
                                        onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                        className="brutalist-input flex-1 text-center text-xl tracking-[0.4em] font-mono"
                                        placeholder="000000" maxLength={6}
                                        data-testid="verify-code-input"
                                    />
                                    <button type="submit" disabled={verifying}
                                        className="bg-[#22c55e] text-white border-4 border-[#0f172a] shadow-[4px_4px_0px_#0f172a] rounded-xl font-bold px-4 py-2 disabled:opacity-50 transition-all" data-testid="verify-submit-btn">
                                        {verifying ? 'Verifying...' : 'Verify'}
                                    </button>
                                </div>
                                <button type="button" onClick={handleResendVerification} disabled={resending}
                                    className="text-sm text-[#475569] hover:text-[#22c55e] font-semibold">
                                    {resending ? 'Sending...' : "Didn't get a code? Resend"}
                                </button>
                            </form>
                        )}
                    </motion.div>
                )}

                {/* Welcome Banner */}
                <motion.div 
                    className="bg-green-300 border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-3xl p-8 mb-10 relative overflow-hidden"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <div className="z-10 relative">
                        <h1 className="font-fredoka text-3xl md:text-4xl font-bold text-[#0f172a] leading-tight">
                            Welcome, {user?.name}!
                        </h1>
                        <p className="text-lg text-[#0f172a]/70 font-bold mt-2">
                            Manage your children's environmental adventures
                        </p>
                    </div>
                    <div className="absolute -right-10 -bottom-10 w-48 h-48 bg-green-400 rounded-full border-4 border-[#0f172a] opacity-40 z-0" />
                </motion.div>

                {/* Children Section */}
                <motion.section 
                    className="mb-10"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                >
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="font-fredoka text-2xl font-bold text-[#0f172a] flex items-center gap-2">
                            Your Rangers
                        </h2>
                        <button
                            onClick={() => setShowAddChild(true)}
                            className="bg-[#22c55e] text-white border-4 border-[#0f172a] shadow-[4px_4px_0px_#0f172a] rounded-xl font-bold px-5 py-2.5 flex items-center gap-2 hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[6px_6px_0px_#0f172a] transition-all"
                            data-testid="add-child-btn"
                        >
                            <Plus weight="bold" size={20} />
                            Add Child
                        </button>
                    </div>

                    {children.length > 0 ? (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {children.map((child, index) => (
                                <motion.div
                                    key={child.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.1 * index }}
                                    className="bg-white border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-2xl p-6 hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[8px_8px_0px_#0f172a] transition-all"
                                    data-testid={`child-card-${index}`}
                                >
                                    <div className="flex items-center gap-4 mb-4 border-b-4 border-[#0f172a] pb-4">
                                        <div className="w-16 h-16 rounded-full bg-white border-4 border-[#0f172a] overflow-hidden">
                                            <RangerAvatar config={child.avatar_config} variant="badge" className="w-full h-full" data-testid={`child-avatar-${index}`} />
                                        </div>
                                        <div>
                                            <h3 className="font-fredoka text-xl font-bold text-[#0f172a]">
                                                {child.name}
                                            </h3>
                                            <p className="text-[#22c55e] font-bold text-sm">
                                                {child.level}
                                            </p>
                                        </div>
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-3 mb-4">
                                        <div className="text-center p-3 bg-yellow-200 border-4 border-[#0f172a] rounded-xl">
                                            <p className="font-fredoka text-2xl font-bold text-[#0f172a]">
                                                {child.xp || 0}
                                            </p>
                                            <p className="text-xs text-[#0f172a]/60 font-bold uppercase tracking-wide">XP</p>
                                        </div>
                                        <div className="text-center p-3 bg-blue-200 border-4 border-[#0f172a] rounded-xl">
                                            <p className="font-fredoka text-2xl font-bold text-[#0f172a]">
                                                {child.badges?.length || 0}
                                            </p>
                                            <p className="text-xs text-[#0f172a]/60 font-bold uppercase tracking-wide">Badges</p>
                                        </div>
                                    </div>

                                    <button
                                        onClick={() => handleSwitchToChild(child.id)}
                                        className="w-full bg-[#38bdf8] text-white border-4 border-[#0f172a] shadow-[4px_4px_0px_#0f172a] rounded-xl font-bold py-2.5 flex items-center justify-center gap-2 hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[6px_6px_0px_#0f172a] transition-all"
                                        data-testid={`switch-to-child-${index}`}
                                    >
                                        <User weight="bold" size={20} />
                                        View as {child.name}
                                    </button>
                                </motion.div>
                            ))}
                        </div>
                    ) : (
                        <div className="bg-white border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-2xl p-12 text-center">
                            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#22c55e]/10 border-4 border-[#0f172a] flex items-center justify-center">
                                <Users weight="duotone" className="text-[#22c55e]" size={40} />
                            </div>
                            <h3 className="font-fredoka text-xl font-bold text-[#0f172a] mb-2">
                                No Rangers Yet
                            </h3>
                            <p className="text-[#475569] font-semibold mb-4">
                                Add your first child to start their environmental adventure!
                            </p>
                            <button
                                onClick={() => setShowAddChild(true)}
                                className="bg-[#22c55e] text-white border-4 border-[#0f172a] shadow-[4px_4px_0px_#0f172a] rounded-xl font-bold px-6 py-3 hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[6px_6px_0px_#0f172a] transition-all"
                            >
                                Add First Ranger
                            </button>
                        </div>
                    )}
                </motion.section>

                {/* Linked Parents Section */}
                <motion.section 
                    className="mb-10"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                >
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="font-fredoka text-2xl font-bold text-[#0f172a]">
                            Parents
                        </h2>
                        <button
                            onClick={() => setShowAddParent(true)}
                            className="bg-[#38bdf8] text-white border-4 border-[#0f172a] shadow-[4px_4px_0px_#0f172a] rounded-xl font-bold px-5 py-2.5 flex items-center gap-2 hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[6px_6px_0px_#0f172a] transition-all"
                            data-testid="add-parent-btn"
                        >
                            <Plus weight="bold" size={20} />
                            Invite Parent
                        </button>
                    </div>

                    {linkedParents.length > 0 ? (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {linkedParents.map((parent, index) => (
                                <div key={parent.id} className="bg-white border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-2xl p-4 flex items-center gap-4" data-testid={`linked-parent-${index}`}>
                                    <div className="w-12 h-12 rounded-full bg-purple-200 border-4 border-[#0f172a] flex items-center justify-center">
                                        <User weight="bold" className="text-[#0f172a]" size={20} />
                                    </div>
                                    <div>
                                        <p className="font-fredoka font-bold text-[#0f172a]">{parent.name}</p>
                                        <p className="text-sm text-[#475569] font-semibold">{parent.email}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="bg-white border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-2xl p-6 text-center">
                            <p className="text-[#475569] font-semibold">
                                No other parents linked yet. Invite a parent to give them access to your children's profiles.
                            </p>
                        </div>
                    )}
                </motion.section>

                {/* National Impact */}
                {impact && (
                    <motion.section 
                        className="mb-10"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                    >
                        <h2 className="font-fredoka text-2xl font-bold text-[#0f172a] mb-6">
                            National Impact
                        </h2>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                            <div className="bg-green-300 border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-2xl p-5 text-center">
                                <p className="font-fredoka text-3xl font-bold text-[#0f172a]">
                                    {impact.total_rangers}
                                </p>
                                <p className="text-[#0f172a]/60 text-sm font-bold uppercase tracking-wide">Total Rangers</p>
                            </div>
                            <div className="bg-blue-200 border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-2xl p-5 text-center">
                                <p className="font-fredoka text-3xl font-bold text-[#0f172a]">
                                    {impact.total_missions_completed}
                                </p>
                                <p className="text-[#0f172a]/60 text-sm font-bold uppercase tracking-wide">Missions Done</p>
                            </div>
                            <div className="bg-yellow-300 border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-2xl p-5 text-center">
                                <p className="font-fredoka text-3xl font-bold text-[#0f172a]">
                                    {impact.total_trees_planted}
                                </p>
                                <p className="text-[#0f172a]/60 text-sm font-bold uppercase tracking-wide">Trees Planted</p>
                            </div>
                            <div className="bg-pink-200 border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-2xl p-5 text-center">
                                <p className="font-fredoka text-3xl font-bold text-[#0f172a]">
                                    {impact.total_rubbish_collected}
                                </p>
                                <p className="text-[#0f172a]/60 text-sm font-bold uppercase tracking-wide">Rubbish Items</p>
                            </div>
                        </div>
                    </motion.section>
                )}

                {/* Quick Links */}
                <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="mb-10"
                >
                    <h2 className="font-fredoka text-2xl font-bold text-[#0f172a] mb-6">
                        Quick Links
                    </h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        <Link to="/missions" data-testid="link-missions">
                            <div className="bg-white border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-2xl p-5 flex items-center justify-between hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[8px_8px_0px_#0f172a] transition-all group">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-green-200 border-4 border-[#0f172a] flex items-center justify-center">
                                        <Target weight="duotone" className="text-[#0f172a]" size={24} />
                                    </div>
                                    <span className="font-fredoka font-bold text-[#0f172a]">Browse Missions</span>
                                </div>
                                <CaretRight weight="bold" className="text-[#0f172a]/40 group-hover:translate-x-1 transition-transform" size={20} />
                            </div>
                        </Link>
                        <Link to="/animals" data-testid="link-animals">
                            <div className="bg-white border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-2xl p-5 flex items-center justify-between hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[8px_8px_0px_#0f172a] transition-all group">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-blue-200 border-4 border-[#0f172a] flex items-center justify-center">
                                        <span className="text-2xl">🐨</span>
                                    </div>
                                    <span className="font-fredoka font-bold text-[#0f172a]">Animal Library</span>
                                </div>
                                <CaretRight weight="bold" className="text-[#0f172a]/40 group-hover:translate-x-1 transition-transform" size={20} />
                            </div>
                        </Link>
                        <Link to="/leaderboard" data-testid="link-leaderboard">
                            <div className="bg-white border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-2xl p-5 flex items-center justify-between hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[8px_8px_0px_#0f172a] transition-all group">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-yellow-200 border-4 border-[#0f172a] flex items-center justify-center">
                                        <Star weight="fill" className="text-[#0f172a]" size={24} />
                                    </div>
                                    <span className="font-fredoka font-bold text-[#0f172a]">Leaderboard</span>
                                </div>
                                <CaretRight weight="bold" className="text-[#0f172a]/40 group-hover:translate-x-1 transition-transform" size={20} />
                            </div>
                        </Link>
                        <Link to="/worksheets" data-testid="link-worksheets">
                            <div className="bg-white border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-2xl p-5 flex items-center justify-between hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[8px_8px_0px_#0f172a] transition-all group">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-orange-200 border-4 border-[#0f172a] flex items-center justify-center">
                                        <Printer weight="duotone" className="text-[#0f172a]" size={24} />
                                    </div>
                                    <span className="font-fredoka font-bold text-[#0f172a]">Worksheets</span>
                                </div>
                                <CaretRight weight="bold" className="text-[#0f172a]/40 group-hover:translate-x-1 transition-transform" size={20} />
                            </div>
                        </Link>
                        <Link to="/games" data-testid="link-games">
                            <div className="bg-white border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-2xl p-5 flex items-center justify-between hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[8px_8px_0px_#0f172a] transition-all group">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-pink-200 border-4 border-[#0f172a] flex items-center justify-center">
                                        <span className="text-2xl">🎮</span>
                                    </div>
                                    <span className="font-fredoka font-bold text-[#0f172a]">Games</span>
                                </div>
                                <CaretRight weight="bold" className="text-[#0f172a]/40 group-hover:translate-x-1 transition-transform" size={20} />
                            </div>
                        </Link>
                        <Link to="/habitats" data-testid="link-habitats">
                            <div className="bg-white border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-2xl p-5 flex items-center justify-between hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[8px_8px_0px_#0f172a] transition-all group">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-green-200 border-4 border-[#0f172a] flex items-center justify-center">
                                        <span className="text-2xl">🌏</span>
                                    </div>
                                    <span className="font-fredoka font-bold text-[#0f172a]">Explore Habitats</span>
                                </div>
                                <CaretRight weight="bold" className="text-[#0f172a]/40 group-hover:translate-x-1 transition-transform" size={20} />
                            </div>
                        </Link>
                        <Link to="/adventure" data-testid="link-adventure">
                            <div className="bg-white border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-2xl p-5 flex items-center justify-between hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[8px_8px_0px_#0f172a] transition-all group">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-emerald-200 border-4 border-[#0f172a] flex items-center justify-center">
                                        <span className="text-2xl">🧭</span>
                                    </div>
                                    <span className="font-fredoka font-bold text-[#0f172a]">Adventure</span>
                                </div>
                                <CaretRight weight="bold" className="text-[#0f172a]/40 group-hover:translate-x-1 transition-transform" size={20} />
                            </div>
                        </Link>
                    </div>
                </motion.section>
            </main>

            {/* Add Child Modal */}
            {showAddChild && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="brutalist-card p-6 md:p-8 w-full max-w-md bg-white"
                    >
                        <h2 className="font-fredoka text-2xl font-bold text-[#1e293b] mb-6">
                            Add New Ranger
                        </h2>
                        
                        <form onSubmit={handleCreateChild} className="space-y-5">
                            <div>
                                <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">
                                    Child's Name
                                </label>
                                <input
                                    type="text"
                                    value={newChildName}
                                    onChange={(e) => setNewChildName(e.target.value)}
                                    className="brutalist-input w-full"
                                    placeholder="Enter name"
                                    data-testid="child-name-input"
                                />
                            </div>

                            <div>
                                <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">
                                    Age
                                </label>
                                <input
                                    type="number"
                                    min="6"
                                    max="13"
                                    value={newChildAge}
                                    onChange={(e) => setNewChildAge(e.target.value)}
                                    className="brutalist-input w-full"
                                    placeholder="Enter age"
                                    data-testid="child-age-input"
                                />
                            </div>

                            <div>
                                <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">
                                    Choose Avatar
                                </label>
                                <div className="grid grid-cols-5 gap-2">
                                    {AVATARS.map((avatar) => (
                                        <button
                                            key={avatar.id}
                                            type="button"
                                            onClick={() => setNewChildAvatar(avatar.id)}
                                            className={`w-14 h-14 rounded-xl border-4 flex items-center justify-center text-2xl transition-all ${
                                                newChildAvatar === avatar.id 
                                                    ? 'border-[#22c55e] bg-[#22c55e]/10 scale-110' 
                                                    : 'border-[#1e293b] hover:border-[#22c55e]'
                                            }`}
                                            data-testid={`avatar-${avatar.id}`}
                                        >
                                            {avatar.emoji}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="flex gap-3 pt-2">
                                <motion.button
                                    type="button"
                                    onClick={() => setShowAddChild(false)}
                                    whileHover={{ y: -2, x: -2 }}
                                    whileTap={{ y: 2, x: 2 }}
                                    className="brutalist-btn flex-1 bg-white text-[#1e293b]"
                                    data-testid="cancel-add-child"
                                >
                                    Cancel
                                </motion.button>
                                <motion.button
                                    type="submit"
                                    disabled={creatingChild}
                                    whileHover={{ y: -2, x: -2 }}
                                    whileTap={{ y: 2, x: 2 }}
                                    className="brutalist-btn flex-1 bg-[#22c55e] text-white disabled:opacity-50"
                                    data-testid="submit-add-child"
                                >
                                    {creatingChild ? 'Creating...' : 'Create Profile'}
                                </motion.button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}

            {/* Add Parent Modal */}
            {showAddParent && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="brutalist-card p-6 md:p-8 w-full max-w-md bg-white"
                    >
                        <h2 className="font-fredoka text-2xl font-bold text-[#1e293b] mb-2">
                            Invite a Parent
                        </h2>
                        <p className="text-[#475569] text-sm mb-6">
                            Send an email invitation. They'll create their own account and be linked to your children.
                        </p>

                        {linkedParents.length > 0 && (
                            <div className="mb-4 p-3 bg-[#f8fafc] rounded-xl">
                                <p className="text-xs font-semibold text-[#94a3b8] mb-2">Linked Parents</p>
                                {linkedParents.map(p => (
                                    <div key={p.id} className="flex items-center gap-2 py-1">
                                        <User weight="bold" size={16} className="text-[#38bdf8]" />
                                        <span className="text-sm font-semibold text-[#1e293b]">{p.name}</span>
                                        <span className="text-xs text-[#94a3b8]">{p.email}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                        
                        <form onSubmit={handleInviteParent} className="space-y-5">
                            <div>
                                <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">
                                    Their Email
                                </label>
                                <input
                                    type="email"
                                    value={inviteEmail}
                                    onChange={(e) => setInviteEmail(e.target.value)}
                                    className="brutalist-input w-full"
                                    placeholder="parent@email.com"
                                    data-testid="invite-email-input"
                                />
                            </div>

                            <div>
                                <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">
                                    Their Name <span className="text-[#94a3b8] font-normal">(optional)</span>
                                </label>
                                <input
                                    type="text"
                                    value={inviteName}
                                    onChange={(e) => setInviteName(e.target.value)}
                                    className="brutalist-input w-full"
                                    placeholder="Parent's name"
                                    data-testid="invite-name-input"
                                />
                            </div>

                            <div className="flex gap-3 pt-2">
                                <motion.button
                                    type="button"
                                    onClick={() => setShowAddParent(false)}
                                    whileHover={{ y: -2, x: -2 }}
                                    whileTap={{ y: 2, x: 2 }}
                                    className="brutalist-btn flex-1 bg-white text-[#1e293b]"
                                    data-testid="cancel-invite-parent"
                                >
                                    Cancel
                                </motion.button>
                                <motion.button
                                    type="submit"
                                    disabled={inviting}
                                    whileHover={{ y: -2, x: -2 }}
                                    whileTap={{ y: 2, x: 2 }}
                                    className="brutalist-btn flex-1 bg-[#38bdf8] text-white disabled:opacity-50"
                                    data-testid="submit-invite-parent"
                                >
                                    {inviting ? 'Sending...' : 'Send Invitation'}
                                </motion.button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}
        </div>
    );
}
